import React, { useEffect, useState } from 'react';
import {
  Globe, Save, Loader2, Check, AlertTriangle, Wifi, WifiOff,
  Eye, EyeOff, TestTube2, RotateCw,
} from 'lucide-react';
import { Card } from '../components/Card';
import { Button } from '../components/Button';
import { SectionHeader } from '../components/Tabs';
import { Badge } from '../components/Badge';
import { Field, Input } from '../components/Input';
import { adminFetch } from '../adminApi';

/**
 * ProxiesPage — operator-only page to manage outbound proxy slots.
 *
 * Phase-7: API has 3 proxy modes (null / classic / stealth), but only
 * classic and stealth need operator-configured outbound proxies:
 *   - classic → operator's residential proxy. Used for proxy_mode=classic
 *               (and proxy_mode=null "dynamic" mode falls back to it
 *               unless the target is known to need Datames).
 *   - stealth → operator's mobile/rotating proxy. Used by Datames for
 *               proxy_mode=stealth requests.
 *
 * Customers NEVER see this page. They just pick a proxy_mode in their
 * API call. Whatever URL is set here is what gets used.
 *
 * Empty URL = direct connection (legacy default).
 */
export default function ProxiesPage() {
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [testResults, setTestResults] = useState({}); // {slot: {ok, latency_ms, exit_ip, error}}
  const [testingAll, setTestingAll] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const data = await adminFetch('/admin/proxy-slots');
      setSlots(data.slots || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  function updateSlotLocal(slotName, patch) {
    setSlots((prev) => prev.map((s) =>
      s.slot === slotName ? { ...s, ...patch } : s
    ));
  }

  async function saveSlot(slotName) {
    const s = slots.find((x) => x.slot === slotName);
    if (!s) return;
    try {
      await adminFetch(`/admin/proxy-slots/${slotName}`, {
        method: 'PATCH',
        body: {
          proxy_url: s.proxy_url,
          protocol: s.protocol,
          notes: s.notes,
        },
      });
      // Refresh to get the new updated_at from server
      await load();
    } catch (e) {
      alert(`Save failed: ${e.message || e}`);
    }
  }

  async function testSlot(slotName) {
    setTestResults((prev) => ({ ...prev, [slotName]: { testing: true } }));
    try {
      const r = await adminFetch(`/admin/proxy-slots/${slotName}/test`, {
        method: 'POST',
      });
      setTestResults((prev) => ({ ...prev, [slotName]: r }));
    } catch (e) {
      setTestResults((prev) => ({
        ...prev,
        [slotName]: { ok: false, error: e.message || String(e) },
      }));
    }
  }

  async function testAll() {
    setTestingAll(true);
    for (const s of slots) {
      await testSlot(s.slot);
    }
    setTestingAll(false);
  }

  const slotMeta = {
    classic: {
      title: 'Classic Mode',
      description: "Operator's residential proxy. Used for proxy_mode=classic (and dynamic mode falls back to it unless the target needs Datames). Pair with a high-quality residential proxy for best results.",
      placeholder: 'http://user:pass@brd.superproxy.io:22225',
    },
    stealth: {
      title: 'Stealth Mode',
      description: "Used with Datames anti-detection. Customers with proxy_mode=stealth use this. Pair with mobile/rotating residential IPs for best results.",
      placeholder: 'http://user:pass@mobile-proxy.com:8080',
    },
  };

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between gap-4">
        <SectionHeader
          title="Proxy Slots"
          description="2 outbound proxy slots. Customers pick classic/stealth in their API call. Whatever URL you set here is what gets used. Empty = direct connection."
        />
        <Button variant="secondary" onClick={testAll} disabled={testingAll || loading}>
          {testingAll ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <TestTube2 className="h-3.5 w-3.5" />}
          Test all
        </Button>
      </div>

      {loading ? (
        <div className="space-y-4">
          {[0, 1].map((i) => (
            <Card key={i}><Card.Body className="h-48 animate-pulse" /></Card>
          ))}
        </div>
      ) : (
        <div className="space-y-4">
          {slots.map((slot) => (
            <ProxySlotCard
              key={slot.slot}
              slot={slot}
              meta={slotMeta[slot.slot]}
              testResult={testResults[slot.slot]}
              onChange={(patch) => updateSlotLocal(slot.slot, patch)}
              onSave={() => saveSlot(slot.slot)}
              onTest={() => testSlot(slot.slot)}
            />
          ))}
        </div>
      )}

      <Card>
        <Card.Body className="bg-surface-2/50 border-t border-border text-xs text-fg-muted space-y-1">
          <div className="flex items-center gap-2">
            <Eye className="h-3.5 w-3.5" />
            <strong className="text-fg">Operator-only:</strong>
            <span>Customers never see this page. They pick classic/stealth in their API request and the engine reads the URL you've configured here.</span>
          </div>
          <div className="flex items-center gap-2">
            <RotateCw className="h-3.5 w-3.5" />
            <strong className="text-fg">Live updates:</strong>
            <span>Changes take effect on the very next request — no restart needed.</span>
          </div>
          <div className="flex items-center gap-2">
            <Globe className="h-3.5 w-3.5" />
            <strong className="text-fg">Empty slot = direct connection.</strong>
            <span>Clear the URL and save to disable the proxy for that mode.</span>
          </div>
        </Card.Body>
      </Card>
    </div>
  );
}

function ProxySlotCard({ slot, meta, testResult, onChange, onSave, onTest }) {
  const [showPassword, setShowPassword] = useState(false);
  const hasUrl = slot.proxy_url && slot.proxy_url.trim().length > 0;

  return (
    <Card>
      <Card.Header>
        <div className="flex-1">
          <Card.Title className="flex items-center gap-2">
            <Globe className="h-4 w-4 text-fg-muted" />
            {meta.title}
          </Card.Title>
          <p className="text-xs text-fg-muted mt-1">{meta.description}</p>
        </div>
        <Badge variant={hasUrl ? 'success' : 'muted'}>
          {hasUrl ? 'Active' : 'Direct (no proxy)'}
        </Badge>
      </Card.Header>
      <Card.Body className="space-y-4 pt-0">
        {/* URL field with show/hide password */}
        <Field label="Proxy URL" htmlFor={`url-${slot.slot}`}>
          <div className="flex gap-2">
            <Input
              id={`url-${slot.slot}`}
              type={showPassword ? 'text' : 'password'}
              value={slot.proxy_url || ''}
              placeholder={meta.placeholder}
              onChange={(e) => onChange({ proxy_url: e.target.value })}
              className="flex-1 font-mono text-xs"
            />
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowPassword((s) => !s)}
              title={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
            </Button>
          </div>
        </Field>

        <div className="grid grid-cols-2 gap-4">
          <Field label="Protocol" htmlFor={`proto-${slot.slot}`}>
            <select
              id={`proto-${slot.slot}`}
              value={slot.protocol || 'http'}
              onChange={(e) => onChange({ protocol: e.target.value })}
              className="w-full h-9 rounded-md bg-surface-2 border border-border px-3 text-sm"
            >
              <option value="http">http</option>
              <option value="https">https</option>
              <option value="socks5">socks5</option>
            </select>
          </Field>
          <Field label="Notes (admin only)" htmlFor={`notes-${slot.slot}`}>
            <Input
              id={`notes-${slot.slot}`}
              value={slot.notes || ''}
              placeholder="e.g. BrightData US residential, $5/GB"
              onChange={(e) => onChange({ notes: e.target.value })}
            />
          </Field>
        </div>

        {/* Test result */}
        {testResult && !testResult.testing && (
          <TestResultRow result={testResult} />
        )}
        {testResult?.testing && (
          <div className="flex items-center gap-2 text-xs text-fg-muted px-3 py-2 rounded-md bg-surface-2 border border-border">
            <Loader2 className="h-3 w-3 animate-spin" />
            Testing connection...
          </div>
        )}

        {/* Last updated */}
        <div className="text-2xs text-fg-subtle">
          Last updated: {slot.updated_at ? new Date(slot.updated_at).toLocaleString() : 'never'}
        </div>
      </Card.Body>
      <Card.Footer className="flex justify-end gap-2">
        <Button variant="secondary" onClick={onTest} disabled={testResult?.testing}>
          {testResult?.testing ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <TestTube2 className="h-3.5 w-3.5" />
          )}
          Test connection
        </Button>
        <Button variant="primary" onClick={onSave}>
          <Save className="h-3.5 w-3.5" />
          Save
        </Button>
      </Card.Footer>
    </Card>
  );
}

function TestResultRow({ result }) {
  if (result.direct) {
    return (
      <div className="flex items-center gap-2 text-xs px-3 py-2 rounded-md bg-surface-2 border border-border">
        <WifiOff className="h-3.5 w-3.5 text-fg-muted" />
        <span className="text-fg-muted">Slot is empty — requests go direct. No proxy to test.</span>
      </div>
    );
  }
  if (result.ok) {
    return (
      <div className="flex items-start gap-2 text-xs px-3 py-2 rounded-md bg-success/10 border border-success/30">
        <Check className="h-3.5 w-3.5 text-success mt-0.5 shrink-0" />
        <div className="space-y-0.5">
          <div className="text-success font-medium">
            Proxy works — {result.latency_ms}ms
          </div>
          {result.exit_ip && (
            <div className="text-fg-muted">
              Exit IP: <span className="font-mono">{result.exit_ip}</span>
            </div>
          )}
        </div>
      </div>
    );
  }
  return (
    <div className="flex items-start gap-2 text-xs px-3 py-2 rounded-md bg-destructive/10 border border-destructive/30">
      <AlertTriangle className="h-3.5 w-3.5 text-destructive mt-0.5 shrink-0" />
      <div className="space-y-0.5">
        <div className="text-destructive font-medium">
          Proxy failed after {result.latency_ms || 0}ms
        </div>
        {result.error && (
          <div className="text-fg-muted font-mono text-2xs break-all">
            {result.error}
          </div>
        )}
      </div>
    </div>
  );
}